import React, { useState, useEffect, useCallback } from 'react';
import { adminGet, adminPost, adminDelete } from '../api';
import ConfirmDialog from './ConfirmDialog';
import MenuItemForm  from './MenuItemForm';

/** Replicate the buildTree logic from src/lib/menuData.js */
function buildTree(rows, parentId = null) {
  return rows
    .filter(r => r.parent_id === parentId)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(r => ({ ...r, children: buildTree(rows, r.id) }));
}

/** Count all descendants */
function countDescendants(item) {
  return (item.children || []).reduce((acc, c) => acc + 1 + countDescendants(c), 0);
}

// ── TreeNode ───────────────────────────────────────────────────

function TreeNode({
  node, siblings, allItems,
  onRefresh, onMove,
  depth = 0,
}) {
  const [open,       setOpen]       = useState(depth < 1);
  const [editing,    setEditing]    = useState(false);
  const [adding,     setAdding]     = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);

  const isLeaf = node.level === 2;
  const idx    = siblings.findIndex(s => s.id === node.id);

  const handleSave = async (updated) => {
    setEditing(false);
    setAdding(false);
    onRefresh();
  };

  const handleDelete = async () => {
    await adminDelete(`/api/admin/menu-items/${node.id}`);
    setDelConfirm(false);
    onRefresh();
  };

  const handleMove = async (direction) => {
    const other = siblings[idx + direction];
    if (!other) return;
    await adminPost('/api/admin/menu-items/reorder', [
      { id: node.id,  sort_order: other.sort_order },
      { id: other.id, sort_order: node.sort_order  },
    ]);
    onRefresh();
  };

  return (
    <div className={`tree-node depth-${depth}`}>
      <div className="tree-row">
        {!isLeaf && (
          <button
            className="tree-toggle btn btn-sm btn-secondary"
            onClick={() => setOpen(o => !o)}
            title={open ? 'Collapse' : 'Expand'}
          >
            {open ? '▾' : '▸'}
          </button>
        )}
        {isLeaf && <span className="tree-toggle-placeholder" />}

        <span className="tree-name">{node.name}</span>
        <span className="tree-meta">id:{node.id} · lvl:{node.level} · {node.type}</span>

        <div className="tree-actions">
          <button className="btn btn-sm btn-secondary" onClick={() => handleMove(-1)} disabled={idx === 0} title="Move up">↑</button>
          <button className="btn btn-sm btn-secondary" onClick={() => handleMove(1)} disabled={idx === siblings.length - 1} title="Move down">↓</button>
          <button className="btn btn-sm btn-secondary" onClick={() => setEditing(true)}>Edit</button>
          {!isLeaf && (
            <button className="btn btn-sm btn-secondary" onClick={() => setAdding(true)}>+ Child</button>
          )}
          <button className="btn btn-sm btn-danger" onClick={() => setDelConfirm(true)}>Delete</button>
        </div>
      </div>

      {editing && (
        <div className="tree-form-wrapper">
          <MenuItemForm
            item={node}
            allItems={allItems}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
          />
        </div>
      )}

      {adding && (
        <div className="tree-form-wrapper">
          <MenuItemForm
            parentId={node.id}
            level={node.level + 1}
            allItems={allItems}
            onSave={handleSave}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {open && !editing && !adding && node.children?.length > 0 && (
        <div className="tree-children">
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              siblings={node.children}
              allItems={allItems}
              onRefresh={onRefresh}
              onMove={onMove}
              depth={depth + 1}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={delConfirm}
        title={`Delete "${node.name}"?`}
        message="This action cannot be undone."
        childCount={countDescendants(node)}
        onConfirm={handleDelete}
        onCancel={() => setDelConfirm(false)}
      />
    </div>
  );
}

// ── MenuTree ───────────────────────────────────────────────────

export default function MenuTree() {
  const [items,    setItems]   = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);
  const [addRoot,  setAddRoot] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGet('/api/admin/menu-items');
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const roots = buildTree(items, null);

  return (
    <div>
      <h2 className="admin-page-title">Menu Items</h2>

      <div className="admin-action-bar">
        <button className="btn btn-primary" onClick={() => setAddRoot(true)}>+ New top-level category</button>
        <button className="btn btn-secondary" onClick={load}>Refresh</button>
      </div>

      {error   && <div className="admin-banner admin-banner-error">{error}</div>}
      {loading && <div style={{ color: '#888' }}>Loading…</div>}

      {addRoot && (
        <div className="tree-form-wrapper" style={{ marginBottom: 20 }}>
          <MenuItemForm
            level={0}
            allItems={items}
            onSave={() => { setAddRoot(false); load(); }}
            onCancel={() => setAddRoot(false)}
          />
        </div>
      )}

      {!loading && roots.length === 0 && !addRoot && (
        <p style={{ color: '#888' }}>No menu items found. Add one above.</p>
      )}

      <div className="tree-container">
        {roots.map(root => (
          <TreeNode
            key={root.id}
            node={root}
            siblings={roots}
            allItems={items}
            onRefresh={load}
            onMove={() => {}}
            depth={0}
          />
        ))}
      </div>

      <style>{`
        .tree-container { background: #fff; border: 1px solid #ddd; border-radius: 6px; overflow: hidden; }
        .tree-node + .tree-node { border-top: 1px solid #f0f0f0; }
        .tree-row {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 14px; flex-wrap: wrap;
        }
        .tree-row:hover { background: #fafbfc; }
        .tree-toggle { min-width: 28px; text-align: center; }
        .tree-toggle-placeholder { display: inline-block; width: 36px; }
        .tree-name { font-weight: 600; font-size: 14px; flex-shrink: 0; }
        .tree-meta { color: #999; font-size: 11px; flex: 1; }
        .tree-actions { display: flex; gap: 5px; flex-wrap: wrap; }
        .tree-children { border-left: 3px solid #e8ecf0; margin-left: 32px; }
        .tree-form-wrapper { padding: 16px; background: #f8f9fc; border-top: 1px solid #eee; }
        .depth-0 > .tree-row { background: #f9fafc; }
        .depth-1 > .tree-row { padding-left: 18px; }
        .depth-2 > .tree-row { padding-left: 14px; }
      `}</style>
    </div>
  );
}
